import type { JtbdData, PersonaData } from '@ispot/shared'

interface Pain { id: string; description: string; severity?: string }
interface Gain { id: string; description: string; importance?: string }

function clip(text: string, maxWords: number): string {
  const words = text.trim().split(/\s+/)
  return words.length <= maxWords ? text.trim() : `${words.slice(0, maxWords).join(' ')}…`
}

/** Short label of the target pain/gain — shown next to a starter affordance.
 *  No mechanical "Tackle:" / "Deliver:" prefixes; the surrounding UI
 *  supplies the verb so each chip reads as a real product line. */
export function painRelieverStarters(pains: Pain[]): Array<{ painId: string; text: string }> {
  return pains.slice(0, 4).map(pain => ({
    painId: pain.id,
    text: clip(pain.description, 10),
  }))
}

export function gainCreatorStarters(gains: Gain[]): Array<{ gainId: string; text: string }> {
  return gains.slice(0, 4).map(gain => ({
    gainId: gain.id,
    text: clip(gain.description, 10),
  }))
}

/** Value proposition draft templates, filled from persona + jtbd context. */
export function valuePropStarters(persona: PersonaData | null, jtbd: JtbdData | null): string[] {
  const role = (persona?.role || 'product teams').toLowerCase()
  const rawJob = jtbd?.title || 'achieve their goals'
  const job = rawJob.charAt(0).toLowerCase() + rawJob.slice(1)
  const functional = jtbd?.functional?.trim()

  const templates = [
    `We help ${role} ${job} by removing the friction that slows them down, so they can focus on the work that matters.`,
    `For ${role} who need to ${job}, we replace tool-hopping with a single grounded workspace.`,
  ]
  if (functional) {
    templates.push(`${functional.charAt(0).toUpperCase() + functional.slice(1)} — built for ${role} who refuse to settle.`)
  }
  return templates
}

/** Benefit starters: transform top gains into outcome statements + universal wins. */
export function benefitStarters(gains: Gain[]): string[] {
  const fromGains = gains.slice(0, 2).map(g => {
    const t = clip(g.description, 10)
    return t.charAt(0).toUpperCase() + t.slice(1)
  })
  const generic = ['Ship 3× faster', 'One source of truth', 'Onboard a new teammate in a day']
  return [...fromGains, ...generic].slice(0, 4)
}

/** Products & services starter chips — generic but concrete. */
export function productServiceStarters(): string[] {
  return ['Core platform', 'Integration adapters', 'Onboarding program']
}
